import type { FactorStateKind } from "../framework";
import { useStateSettingsContext } from "../api/StateSettingsContext";

import riskIcon from "../assets/state-icons/risk_64.png";
import changeIcon from "../assets/state-icons/change_64.png";
import protectiveIcon from "../assets/state-icons/protective_64.png";
import notAssessedIcon from "../assets/state-icons/not-assessed_64.png";

/**
 * Icons for the four factor states.
 *
 * Renders the admin-uploaded custom SVG when provided (via the customSvg prop
 * or the GlobalStateStyles settings); otherwise renders the v4 factor-state
 * artwork as a 64px PNG (see STATE_ICON_SRC). The artwork is NASH's commissioned
 * Law Creative mangōpare design (LC11225), recoloured per state:
 *
 *   - Risk         Te Korenga                       — red
 *   - Change       Te Aranga Ake / Te Ohonga Ake    — amber
 *   - Protective   Te Mōhiotanga / Te Māramatanga   — green
 *   - Not assessed Kāore anō i aromatawaihia         — grey
 *
 * Colour is baked into the artwork, so (unlike the old monochrome glyphs) these
 * do not follow the --state-* CSS tokens. Change and Protective share a
 * silhouette and differ only by colour, so callers must always pair the icon
 * with the state label / te reo name (they do). The image is decorative by
 * default and only gets an accessible name when ariaLabel is supplied. Custom
 * SVGs are sanitised server-side before storage and rendered via
 * dangerouslySetInnerHTML.
 */

const STATE_ICON_SRC: Record<FactorStateKind, string> = {
  risk: riskIcon,
  change: changeIcon,
  protective: protectiveIcon,
  "not-assessed": notAssessedIcon,
};

type Props = {
  state: FactorStateKind;
  className?: string;
  /** Optional accessible name. Default is hidden (decorative). */
  ariaLabel?: string;
  /** Admin-provided custom SVG markup. Overrides the default if present. */
  customSvg?: string | null;
};

export function StateIcon({ state, className, ariaLabel, customSvg }: Props) {
  const settings = useStateSettingsContext();
  const effectiveCustomSvg = customSvg ?? settings.customSvgFor(state);
  const decorative = !ariaLabel;

  if (effectiveCustomSvg && effectiveCustomSvg.trim().length > 0) {
    // The SVG was sanitised on the server before being stored. Render it inline
    // so it inherits currentColor for fill/stroke and can be sized via CSS the
    // same way the PNG default is.
    const a11y = decorative
      ? { "aria-hidden": true as const }
      : { role: "img" as const, "aria-label": ariaLabel };
    return (
      <span
        className={`state-icon state-icon--custom ${className ?? ""}`.trim()}
        dangerouslySetInnerHTML={{ __html: effectiveCustomSvg }}
        {...a11y}
      />
    );
  }

  return (
    <img
      src={STATE_ICON_SRC[state]}
      alt={ariaLabel ?? ""}
      className={`state-icon state-icon--v4 ${className ?? ""}`.trim()}
      width={64}
      height={64}
      draggable={false}
      {...(decorative ? { "aria-hidden": true as const } : {})}
    />
  );
}

export default StateIcon;
